import { useEffect } from 'react';
import { Undo2 } from 'lucide-react';
import { useUndoStore } from '../../lib/undoStack';
import { cn } from '../../lib/utils';

/** How long an undo/redo toast lingers before auto-dismissing. */
const TOAST_TTL_MS = 4000;

/**
 * Global snackbar for app-level Undo / Redo feedback. Subscribes to the
 * single undo store so the message follows whichever surface triggered
 * the action. Mount once near the project shell ({@link ProjectLayout}).
 */
export function UndoToast() {
  const toast = useUndoStore((s) => s.toast);
  const dismiss = useUndoStore((s) => s.dismissToast);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(dismiss, TOAST_TTL_MS);
    return () => clearTimeout(timer);
  }, [toast, dismiss]);

  if (!toast) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="undo-toast"
      className={cn(
        'fixed bottom-6 left-1/2 z-50 flex max-w-[90vw] -translate-x-1/2 items-center gap-2 rounded-md px-4 py-2 text-sm text-white shadow-lg',
        toast.kind === 'error' ? 'bg-red-600' : 'bg-slate-800',
      )}
    >
      <Undo2 className="h-4 w-4 shrink-0" aria-hidden />
      <span className="truncate">{toast.message}</span>
    </div>
  );
}
